// # Filename: src/components/games/GameBoardFrame.jsx
//
// Shared layout frame for the simple board games (Checkers, Connect Four, Sudoku):
// centers the status/turn bar + board + any below-board content (result card, "New
// game" link) as one group in the space between the header and the bottom nav.
//
// Below 1024px (where BottomTabBar is visible -- it's `lg:hidden`), position/size come
// from directly measuring the ALREADY-RENDERED header and bottom-nav elements'
// getBoundingClientRect(), not from assumed constants. Previous versions computed the
// frame's top/height from hardcoded numbers (a 60/64px "documented" header height, a
// 92px "matches LayoutFrame's reserve" bottom-nav guess) or from window.visualViewport
// combined with those same guessed constants. On a real device, every one of those
// assumptions turned out to not match reality closely enough -- content rendered
// flush against the header with no gap, or clipped against the bottom nav. The header
// and BottomTabBar are real elements that are ALREADY correctly visible on screen in
// every screenshot from this device; asking the DOM directly where they actually are
// removes the guessing entirely, the same way useMeasuredAvailableHeight already does
// for board sizing elsewhere in this codebase.
//
// window.visualViewport is still used, only as a safety clamp: if BottomTabBar's own
// `position: fixed` bottom edge were ever positioned outside the actually-visible area
// (the historical "fixed resolves against the layout viewport, not the visual one" iOS
// Safari behavior this whole investigation is about), the frame's bottom edge is
// clamped to whatever's really visible rather than trusting nav's rect blindly.
//
// At 1024px+ (true desktop: sidebar layout, no bottom nav), the frame's own DOM
// output reverts to normal flow (`lg:static`, no fixed top/height) and relies on
// LayoutFrame's ancestor `h-full` chain for positioning -- that part is unaffected by
// screen height. `frameHeight` is still real at this breakpoint (measured off
// LayoutFrame's `#app-scroll-frame`, not hardcoded to 0): a page's own content can be
// taller than a short-but-wide ("MacBook-class") desktop viewport even though it fits
// width-wise, and this component's unconditional `overflow-hidden` clips that excess
// instead of the ancestor scrolling to it -- so any page capping board size against
// `frameHeight` (e.g. SudokuPage) needs a non-zero number here to actually work.
//
// Vertical distribution below 1024px is NOT plain `justify-center`: on Checkers/Connect
// Four the content group (status bar + board [+ result card]) is usually shorter than
// frameHeight, and a dead-even split reads as "the board floats too high" once you
// account for the board's own visual weight sitting above its midline. Two `flex-grow`
// spacer divs bracket the content instead of relying on `justify-content`, with the top
// spacer given a bigger share (0.56) than the bottom (0.44) -- a slight downward bias so
// the group sits closer to how Sudoku's board+number-pad group naturally reads between
// header and bottom nav. Spacers are skipped entirely on desktop so the `lg:` path is
// untouched.
//
// `useGameBoardFrameMetrics` is exported so a page can read the same real frameHeight
// this component measures (e.g. SudokuPage caps its board size against it) instead of
// re-deriving "available height" from sibling refs that sandwich the board itself --
// that pattern measures the board's own current size, not the true available space.

import { useLayoutEffect, useState } from "react";

const DESKTOP_BREAKPOINT_PX = 1024; // matches BottomTabBar's `lg:hidden`
const FALLBACK_HEADER_HEIGHT_PX = 60; // only used before header/nav exist in the DOM at all
const BOTTOM_BREATHING_ROOM_PX = 12; // small gap so content doesn't touch BottomTabBar's edge
const DESKTOP_FRAME_PADDING_PX = 40; // matches this component's own `lg:pt-6 lg:pb-4` (24 + 16)

function readFrameMetrics() {
  if (typeof window === "undefined") {
    return { frameTop: FALLBACK_HEADER_HEIGHT_PX, frameHeight: 0, isDesktop: false };
  }

  const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;
  if (isDesktop) {
    // GameBoardFrame's own positioning at this breakpoint is untouched (still
    // `lg:static`, no fixed top/height below) -- but pages like SudokuPage read
    // `frameHeight` from this hook to cap board size against real available space.
    // A short-but-wide ("MacBook-class") desktop viewport can be shorter than a
    // board's natural min(90vw, 620px)-ish size; without a real number here that
    // capping silently no-ops (falsy 0 short-circuits it in the consuming page),
    // and the oversized content then gets clipped by this component's own
    // unconditional `overflow-hidden` instead of scrolling. LayoutFrame's scrollable
    // content div (`#app-scroll-frame`) is the real bounding box for this content on
    // desktop -- same "measure the actual rendered element" approach as the
    // header/nav measurement below, just for the one breakpoint that used to skip it.
    const scrollFrame = document.getElementById("app-scroll-frame");
    if (!scrollFrame) {
      return { frameTop: 0, frameHeight: 0, isDesktop: true };
    }
    const rect = scrollFrame.getBoundingClientRect();
    const style = window.getComputedStyle(scrollFrame);
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    return {
      frameTop: rect.top + paddingTop,
      frameHeight: Math.max(0, rect.height - paddingTop - paddingBottom - DESKTOP_FRAME_PADDING_PX),
      isDesktop: true,
    };
  }

  const header = document.querySelector("header");
  const nav = document.querySelector("nav");
  const visibleBottom = window.visualViewport
    ? window.visualViewport.offsetTop + window.visualViewport.height
    : window.innerHeight;

  const frameTop = header ? header.getBoundingClientRect().bottom : FALLBACK_HEADER_HEIGHT_PX;
  const navTop = nav ? nav.getBoundingClientRect().top : visibleBottom;
  const frameBottom = Math.min(navTop, visibleBottom);

  return {
    frameTop,
    frameHeight: Math.max(0, frameBottom - frameTop - BOTTOM_BREATHING_ROOM_PX),
    isDesktop: false,
  };
}

export function useGameBoardFrameMetrics() {
  // Lazy initializer runs synchronously during the first render. header/nav aren't in
  // the real DOM yet on a true first mount (React builds the whole tree in memory
  // before committing), so this falls back to FALLBACK_HEADER_HEIGHT_PX at that exact
  // instant -- but useLayoutEffect below re-measures and corrects state before the
  // browser paints anything, so there's no visible flash of the wrong size.
  const [metrics, setMetrics] = useState(readFrameMetrics);

  useLayoutEffect(() => {
    const update = () => setMetrics(readFrameMetrics());
    update();
    const vv = window.visualViewport;
    window.addEventListener("resize", update);
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
    };
  }, []);

  return metrics;
}

export default function GameBoardFrame({ children }) {
  const { frameTop, frameHeight, isDesktop } = useGameBoardFrameMetrics();

  return (
    <div
      className="
        overflow-hidden px-1 sm:px-4
        pb-[env(safe-area-inset-bottom)]
        flex flex-col items-center justify-center
        lg:static lg:h-full lg:min-h-0 lg:pt-6 lg:pb-4
      "
      style={
        isDesktop
          ? undefined
          : { position: "fixed", left: 0, right: 0, top: `${frameTop}px`, height: `${frameHeight}px` }
      }
    >
      {!isDesktop && <div aria-hidden="true" style={{ flex: "0.56 0 0%" }} />}
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center gap-3 sm:gap-5 shrink-0">
        {children}
      </div>
      {!isDesktop && <div aria-hidden="true" style={{ flex: "0.44 0 0%" }} />}
    </div>
  );
}
